import React, { useContext } from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'
import YotpoStars from 'src/components/YotpoStars'
import StoreContext from '../../context/StoreContext'

import {
  Section,
  JumbotronImageContainer,
  ContentContainer,
  Headline,
  Title,
  Text,
  CtaContainer,
  CenteredMobile,
  BuyNowLink,
  TitleContainer,
  SyncsSectionContainer,
  LogoImageContainer,
  MobileSyncsSection,
  Intro,
  Wirecutter,
  MDBadge,
} from './styles'
import {
  CAL_BUY_LINK,
  CAL_MAX_BUY_LINK,
  IS_FOREIGN_COUNTRY,
  IN_SEASONAL_STATE,
  VIP_CODE,
  CAL_MAX_ON_SALE,
} from '../../utils/constants'
import { SyncsHeader } from '../HomepageCalHero/styles'

const CalMaxHero = ({ calendar, homepage }) => {
  const {
    calendarHero,
    calendarUSAHero,
    calendarHeroMobile,
    calendarUSAHeroMobile,
    calendarMaxHero,
    calendarMaxHeroMobile,
    homepageHero,
    homepageHeroMobile,
    outlook,
    google,
    apple,
    cozi,
    yahoo,
    wirecutter,
    homepageMDHeroDesktop,
    homepageMDHeroMobile,
    md2026salebadge,
  } = useStaticQuery(graphql`
    query calMaxHeroQuery {
      calendarHero: file(relativePath: { eq: "bfcm2025/bau_cal_desktop.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      calendarHeroMobile: file(relativePath: { eq: "bfcm2025/bau_cal_mobile.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      calendarUSAHero: file(relativePath: { eq: "bfcm2025/bau_cal_usa_desktop.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      calendarUSAHeroMobile: file(relativePath: { eq: "bfcm2025/bau_cal_usa_mobile.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      calendarMaxHero: file(relativePath: { eq: "bfcm2025/bau_cal_max_desktop.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      calendarMaxHeroMobile: file(relativePath: { eq: "bfcm2025/bau_cal_max_mobile.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      homepageHero: file(relativePath: { eq: "homepage/calendar-2-homepage-white.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      homepageHeroMobile: file(
        relativePath: { eq: "homepage/calendar-2-homepage-white-mobile.webp" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      google: file(relativePath: { eq: "icons/google.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      outlook: file(relativePath: { eq: "icons/outlook.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      apple: file(relativePath: { eq: "icons/apple.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      cozi: file(relativePath: { eq: "icons/cozi.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      yahoo: file(relativePath: { eq: "icons/yahoo.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      wirecutter: file(relativePath: { eq: "homepage/press/wirecutter-NYT.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      homepageMDHeroDesktop: file(relativePath: { eq: "md2026/calendar-hp-mday.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      homepageMDHeroMobile: file(relativePath: { eq: "md2026/calendar-hp-mday-mobile.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      md2026salebadge: file(relativePath: { eq: "md2026/md2026_sale_badge.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
    }
  `)
  const {
    store: {
      banner: { promoCode },
    },
  } = useContext(StoreContext)
  const inSeasonalState = (IN_SEASONAL_STATE || promoCode === VIP_CODE) && (calendar || homepage)

  let commonText = {
    title: calendar
      ? !IS_FOREIGN_COUNTRY
        ? `<span>Calendar 2 <br/> Has Arrived</span>`
        : `<span>Families in Sync, <br/> in a Blink</span>`
      : homepage
      ? 'Families In Sync Can Do Beautiful Things'
      : `<span>Maximize Your Family's Potential</span>`,
    text: homepage
      ? 'Our best-selling Calendar promotes teamwork through auto-synced schedules and interactive chore charts.'
      : `Get the whole family working as one with synced schedules, chore charts, customizable lists and more — all on one beautiful ${
          calendar ? '15″' : '27″'
        } display.`,
    cta: homepage ? 'Shop Calendar' : 'Buy Now',
    productId: '6863425175604',
    calendartext: !IS_FOREIGN_COUNTRY
      ? 'The all-in-one family hub loved by 1 million+ families has a new look and upgraded hardware.'
      : 'Color-coded schedules, chore charts, customizable lists (and more!) are ready to get everyone on the same page.',
  }

  const calendarList = [
    { id: 'google', label: 'Google', image: google },
    { id: 'outlook', label: 'Outlook', image: outlook },
    { id: 'apple', label: 'Apple', image: apple },
    { id: 'cozi', label: 'Cozi', image: cozi },
    { id: 'yahoo', label: 'Yahoo', image: yahoo },
  ]

  let desktopImage = calendar
    ? !IS_FOREIGN_COUNTRY
      ? calendarUSAHero
      : calendarHero
    : homepage
    ? homepageHero
    : calendarMaxHero
  let mobileImage = calendar
    ? !IS_FOREIGN_COUNTRY
      ? calendarUSAHeroMobile
      : calendarHeroMobile
    : homepage
    ? homepageHeroMobile
    : calendarMaxHeroMobile

  if (inSeasonalState && !calendar) {
    commonText = {
      title: 'For the Mom <br/> Who Does It All',
      text: 'This Mother’s Day, surprise her with the best-selling calendar keeping families connected and schedules in sync.',
      cta: 'Shop Calendar',
    }
    desktopImage = homepageMDHeroDesktop
    mobileImage = homepageMDHeroMobile
  }

  return (
    <>
      <Section>
        <JumbotronImageContainer homepage={homepage}>
          <GatsbyImage
            image={desktopImage.childImageSharp.gatsbyImageData}
            alt="Skylight Calendar Max displayed on wall"
            loading="eager"
          />
          <GatsbyImage
            image={mobileImage.childImageSharp.gatsbyImageData}
            alt="Skylight Calendar Max displayed on wall"
            loading="eager"
          />
          {(inSeasonalState || CAL_MAX_ON_SALE) && (
            <MDBadge>
              <GatsbyImage
                image={md2026salebadge.childImageSharp.gatsbyImageData}
                alt="Skylight Calendar is part of the Mother's Day Sale"
                loading="eager"
              />
            </MDBadge>
          )}
        </JumbotronImageContainer>
        <ContentContainer homepage={homepage} md={inSeasonalState && !calendar}>
          {!homepage && (
            <Headline>
              <YotpoStars
                color="#000"
                textcolor={!IS_FOREIGN_COUNTRY && calendar ? '#fff' : '#444444'}
                productId={commonText.productId}
              />
            </Headline>
          )}
          {homepage && <Intro homepage>CALENDAR 2</Intro>}
          <TitleContainer>
            <Title
              white={!IS_FOREIGN_COUNTRY && calendar}
              calendar={calendar}
              homepage={homepage}
              dangerouslySetInnerHTML={{ __html: commonText.title }}
            />
          </TitleContainer>
          <Text
            white={!IS_FOREIGN_COUNTRY && calendar}
            homepage={homepage}
            dangerouslySetInnerHTML={{
              __html: calendar ? commonText.calendartext : commonText.text,
            }}
          />
          <CtaContainer>
            <CenteredMobile homepage={homepage} className="desktopCta">
              <BuyNowLink
                homepage={homepage}
                to={calendar || homepage ? CAL_BUY_LINK : CAL_MAX_BUY_LINK}
              >
                {commonText.cta}
              </BuyNowLink>
            </CenteredMobile>
          </CtaContainer>
          {!homepage && (
            <SyncsSectionContainer calendar={calendar}>
              <SyncsHeader white={!IS_FOREIGN_COUNTRY && calendar}>
                Seamlessly auto-syncs with:
              </SyncsHeader>
              <CtaContainer>
                {calendarList.map(({ id, label, image }) => (
                  <LogoImageContainer>
                    <GatsbyImage
                      key={id}
                      image={image.childImageSharp.gatsbyImageData}
                      alt={`Skylight Calendar is compatible with ${label}`}
                    />
                  </LogoImageContainer>
                ))}
              </CtaContainer>
            </SyncsSectionContainer>
          )}
        </ContentContainer>
        {homepage && (
          <Wirecutter>
            <GatsbyImage
              image={wirecutter.childImageSharp.gatsbyImageData}
              alt="Featured in The New York Times Wirecutter"
              loading="eager"
            />
          </Wirecutter>
        )}
      </Section>

      {!homepage && (
        <MobileSyncsSection>
          <CtaContainer>
            <SyncsHeader green>Auto-syncs with:</SyncsHeader>
            {calendarList.map(({ id, label, image }) => (
              <LogoImageContainer>
                <GatsbyImage
                  key={id}
                  image={image.childImageSharp.gatsbyImageData}
                  alt={`Skylight Calendar is compatible with ${label}`}
                />
              </LogoImageContainer>
            ))}
          </CtaContainer>
        </MobileSyncsSection>
      )}
    </>
  )
}

CalMaxHero.propTypes = {
  calendar: PropTypes.bool,
  homepage: PropTypes.bool,
}
export default CalMaxHero
